import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Hero from '../components/Hero';
import Card from '../components/Card';
import { ShieldCheck, ArrowRight, CheckCircle2, Phone, HelpCircle, Activity, Award, Zap } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { INSURANCE_PRODUCTS } from '../data/insuranceData';
import './Insurance.css';

const ProductPage = () => {
    const { productId } = useParams();
    const { t } = useLanguage();
    const navigate = useNavigate();
    
    const product = INSURANCE_PRODUCTS[productId];

    useEffect(() => {
        if (!product) {
            navigate('/contact');
        }
        window.scrollTo(0, 0);
    }, [productId, product, navigate]);

    if (!product) return null;

    const ProductIcon = product.icon;

    return (
        <div className="insurance-page product-detail-page">
            <Hero 
                title={t(product.key)}
                subtitle={`The world's most trusted ${t(product.category).toLowerCase()} solution. Elite protection designed for your unique needs with industry-leading claim settlement.`}
                bgGradient={true}
                image="/assets/product-hero.png"
            />

            <section className="section-padding container">
                <div className="grid grid-2" style={{ gap: '60px', alignItems: 'center' }}>
                    <div className="product-info-content">
                        <div className="tag-premium">PREMIUM PROTECTION</div>
                        <h2 className="section-title">{t(product.key)} Overview</h2>
                        <p className="description-large">
                            {t(product.key)} is an elite insurance solution designed to provide the highest level 
                            of security for your family and assets. With <strong>Bharatinsurex</strong>, 
                            you're not just buying a policy; you're investing in peace of mind.
                        </p>
                        
                        <div className="benefit-bullets">
                            <div className="bullet-item">
                                <CheckCircle2 className="bullet-icon" />
                                <span><strong>High Sum Insured:</strong> Up to ₹50 Crores coverage.</span>
                            </div>
                            <div className="bullet-item">
                                <CheckCircle2 className="bullet-icon" />
                                <span><strong>Global Coverage:</strong> Protection that travels with you.</span>
                            </div>
                            <div className="bullet-item">
                                <CheckCircle2 className="bullet-icon" />
                                <span><strong>Fast Claims:</strong> Approved in under 60 minutes.</span>
                            </div>
                            <div className="bullet-item">
                                <CheckCircle2 className="bullet-icon" />
                                <span><strong>Zero Paperwork:</strong> 100% digital onboarding.</span>
                            </div>
                        </div>

                        <div className="action-row" style={{ marginTop: '40px', display: 'flex', gap: '20px' }}>
                            <button className="btn btn-primary" onClick={() => navigate('/contact')}>Get Insured Now</button>
                        </div>
                    </div>
                    
                    <div className="product-visual glass">
                        <div className="visual-icon-host">
                            <ProductIcon size={120} />
                        </div>
                        <div className="stat-pill top">99.7% Claim Ratio</div>
                        <div className="stat-pill bottom">24/7 Support Active</div>
                    </div>
                </div>
            </section>

            <section className="section-padding" style={{ background: '#F8F9FA' }}>
                <div className="container">
                    <div className="text-center mb-60">
                        <h2 className="section-title">Core Benefits</h2>
                        <p className="section-subtitle">Why elite policyholders choose our {t(product.key)} over others.</p>
                    </div>
                    
                    <div className="grid grid-3">
                        <Card title="Instant Issuance" description="Get your policy document delivered to your email in under 2 minutes." icon={Zap} />
                        <Card title="Expert Support" description="Dedicated relationship managers for seamless claim settlements." icon={Award} />
                        <Card title="Flexible Premiums" description="Choose monthly, quarterly or yearly payment cycles at zero markup." icon={Activity} />
                    </div>
                </div>
            </section>

            <div className="container text-center section-padding">
                <div className="cta-box glass">
                    <h3>Frequently Asked Questions</h3>
                    <p>Have questions about {t(product.key)}? Our experts have the answers.</p>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '30px', marginTop: '30px' }}>
                        <a href="tel:+919667193590" style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'white' }}>
                            <Phone size={20} /> Call Expert
                        </a>
                        <Link to="/faqs" style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'white' }}>
                            <HelpCircle size={20} /> Ask a Question
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductPage;
